//
// # Schematic Pin (Instance)
//

// Local Imports
import { graphics, model } from "GenAlphaModel";
import { MousePos } from "../../../mousepos";
import { EntityKind } from "../../../entity";
import { TextLabel, SymbolPin } from "../../shared";
import { Base } from "./base";
import * as style from "../../../style";
import type { DrawTo } from "../../../draw";
import { StyleTables } from "../../shared/styles";
import { Entity, Args } from "../../../tree";
import { SelectionBox } from "./selbox";

// # Schematic Pin
//
// An instance-like object with a drawing and location,
// which annotates a net as being externally accessible.
//
export class SchPin extends Base<Data> {
  readonly entityKind = EntityKind.SchPin;
  public label: TextLabel | null = null;
  public symbolPin!: SymbolPin;
  public symbol!: SchPinSymbol;
  public selBoxEntity!: SelectionBox;
  xformedSelBox!: model.Bbox; // Transformed selection box

  constructor(args: Args<Data>) {
    super(args);
    this.build();
  }

  // Clear out all our sub data stores
  override clear = () => {
    super.clear();
    this.label = null;
  };

  // Build our sub data stores from our `data`.
  override build = () => {
    const symbol = Data.symbol(this.data);
    const { labelFmt } = this.data;
    const { symbolPin } = symbol;

    this.symbol = new SchPinSymbol({
      data: symbol,
      parent: this,
    });

    this.selBoxEntity = new SelectionBox({
      data: symbol.selectionBox,
      parent: this,
    });

    // Add the instance-port
    this.symbolPin = new SymbolPin({ data: symbolPin, parent: this });

    if (labelFmt) {
      // Combine our `portName` with our label-format to create a text label
      const textData = graphics.Text.new({
        text: Data.portName(this.data),
        ...labelFmt,
      });
      this.label = new TextLabel({
        data: { ...textData, labelKind: model.ExprKind.PortName },
        parent: this,
      });
    }
    this.updateBbox();
  };

  override redraw = (to: DrawTo): unknown => {
    return to.group(this.data.xform, this.redraw_);
  };

  // The part of `redraw` that happens inside our transformed coordinate space
  private readonly redraw_ = (to: DrawTo): unknown => {
    this.symbol.redraw(to);
    this.selBoxEntity.redraw(to);
    this.symbolPin?.redraw(to);
    return this.label?.redraw(to);
  };

  // Bounding box in schematic coordinates
  get bbox(): model.Bbox {
    return this.xformedSelBox;
  }

  // Boolean indication of whether `mousePos` is inside the Instance's bounding box.
  // Test against all of the Instance's `SelectionBox`es.
  hitTest = (mousePos: MousePos): boolean => {
    return model.Bbox.contains(this.xformedSelBox, mousePos.sch);
  };

  // Box-drag selection: test against the transformed selection box.
  intersectsBbox = (bbox: model.Bbox): boolean => {
    return model.Bbox.intersects(bbox, this.xformedSelBox);
  };

  // Update the cached/ transformed selection boxes
  updateBbox = () => {
    const { selectionBox } = Data.symbol(this.data);
    this.xformedSelBox = model.Xform.transformBbox(
      this.data.xform,
      selectionBox
    );
  };
}

export type Data = model.schematic.SchPinSpec;
export const Data = {
  portName: (data: Data) => data.portName,
  symbol: (data: Data): model.schematic.SchPinSymbol => {
    return model.schematic.SchPinSymbol.get(data.direction);
  },
};

class SchPinSymbol extends Entity<model.schematic.SchPinSymbol> {
  override readonly cache = this.makeCache<style.Style>(StyleTables.symbol);
  override redraw = (to: DrawTo): unknown => {
    return to.elements(this.data.graphics, this.cache.style, this);
  };
}
